import { get, postJSON, putJSON, remove, patchJSON } from "api/base";
import { isProdEnvironment } from "util/env-utils";
import CONSTANTS from "const/constants";
import type {
    CredentialType,
    RTMPIntegrationData,
    SharedIntegrationResponse
} from "api/integrations.types";
import type { Organization, SamlConfiguration } from "api/organizations.types";
import type { IntegrationData } from "./integrations.types";
import type { S3IntegrationOrgData } from "pages/organization/components/integrations/integrations.types";

export * from "api/organizations.types";

const { CREDENTIAL_TYPES } = CONSTANTS;

export const ORGANIZATION_ENDPOINT = `v1/organizations`;

/**
 * @summary - Get a list of all orgs. You need to be an ADMIN user in the Grabyo Admin org to access this.
 */
export const getAllOrganizations = async (): Promise<
    SharedIntegrationResponse[] | any
> => {
    if (isProdEnvironment) {
        try {
            const {
                data: { data }
            } = await get(ORGANIZATION_ENDPOINT);
            return data;
        } catch ({ response }) {
            if (response.status !== 403) {
                console.error(`Failed to get organizations`, response.status);
                return Promise.reject(response);
            }
        }
    }

    return [];
};

/**
 * @summary - Sets the MFA enabled property for an organization
 */
export const setOrganizationMfa = async (
    id: string,
    requestData: { isMfaEnabled: boolean }
): Promise<Organization | any> => {
    const {
        data: { data }
    } = await postJSON(`${ORGANIZATION_ENDPOINT}/${id}/mfa`, {
        ...requestData
    });

    return data;
};

/**
 * @summary - Creates a new organization credential
 * @return The new organization credential
 */
export const createOrganizationCredential = async (
    requestData: IntegrationData
): Promise<SharedIntegrationResponse | any> => {
    const payload = await postJSON(`${ORGANIZATION_ENDPOINT}/credentials`, {
        ...requestData
    });

    return payload;
};

/**
 * Amends organization credentials with patch request
 */
export const patchOrganizationCredential = async (
    organizationCredentialId: string,
    requestData: Partial<
        IntegrationData | RTMPIntegrationData | S3IntegrationOrgData
    > //TODO: should this be a deep partial?
): Promise<SharedIntegrationResponse> => {
    const payload = (await patchJSON(
        `${ORGANIZATION_ENDPOINT}/credentials/${organizationCredentialId}`,
        {
            ...requestData
        }
    )) as unknown as SharedIntegrationResponse;

    return payload;
};

/**
 * @summary - Returns a list of organization credentials
 * @return An array of organization credentials
 */
export const getAllOrganizationCredentials = async (): Promise<
    SharedIntegrationResponse[]
> => {
    const {
        data: { data }
    } = await get(`${ORGANIZATION_ENDPOINT}/credentials`);
    return data;
};

/**
 * @summary - Grants access to an organization credential for one or more users
 * @param
 * @param  An array of userId's
 * @return The updated organization credential
 */
export const grantAccessToOrganizationCredential = async (
    organizationCredentialId: string,
    allowedUsers: string[]
): Promise<SharedIntegrationResponse | any> => {
    const {
        data: { data }
    } = await putJSON(
        `${ORGANIZATION_ENDPOINT}/credentials/${organizationCredentialId}`,
        {
            access: [...allowedUsers]
        }
    );
    return data;
};

/**
 * @summary - Update an organization credential
 * @param organizationCredentialId
 * @param credential data to be updated. NOTE: User access can be updated
 * for any shared credentials. However, displayName and integration attibuttes can only be
 * updated for certain credential types (i.e. FTP, S3, CustomRTMP, Akamai CustomRTMP, Brightcove,
 * Comcast (ThePlatform) and OPTA)
 */
export const updateOrganizationCredential = async (
    organizationCredentialId: string,
    requestData: { access: string }
): Promise<SharedIntegrationResponse | any> => {
    const {
        data: { data }
    } = await putJSON(
        `${ORGANIZATION_ENDPOINT}/credentials/${organizationCredentialId}`,
        requestData
    );
    return data;
};

/**
 * @summary - Refresh an organization credential
 * @return The refreshed organization credential
 */
export const refreshOrganizationCredential = async (
    organizationCredentialId: string
): Promise<SharedIntegrationResponse | any> => {
    const {
        data: { data }
    } = await patchJSON(
        `${ORGANIZATION_ENDPOINT}/credentials/${organizationCredentialId}?refresh=true`
    );
    return data;
};

/**
 * @summary - Transfer ownership of an organization credential to another user
 * @param userCredentialId
 * @param credentialType - OneOf [CREDENTIAL_TYPES]
 */
export const transferOrganizationCredentialOwnership = async (
    userCredentialId: string,
    credentialType: CredentialType
): Promise<SharedIntegrationResponse | any> => {
    const requestData =
        credentialType === CREDENTIAL_TYPES.CUSTOM_RTMP
            ? { userCustomRtmpCredentialId: userCredentialId }
            : { userSocialCredentialId: userCredentialId };

    const {
        data: { data }
    } = await postJSON(`${ORGANIZATION_ENDPOINT}/credentials`, requestData);
    return data;
};

/**
 * @summary - Deletes an organization credential
 */
export const deleteOrganizationCredential = async (id): Promise<any> => {
    return await remove(`${ORGANIZATION_ENDPOINT}/credentials/${id}`);
};

export const getOrganizationPreferences = async () => {
    const { data } = await get(
        `${window.location.origin}/media-transport/api/v1/organization/me/preferences`
    );
    return data;
};

export const getOrganizationSamlConfiguration = async (
    id: string
): Promise<SamlConfiguration> => {
    const { data } = await get(`${ORGANIZATION_ENDPOINT}/${id}/saml`);
    return data;
};
